import { useTheme } from '@mui/material';
import {
  LocalVideoTrack,
  Mutex,
  VideoPresets,
  createLocalVideoTrack,
} from 'livekit-client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { getVideoFilter } from 'src/provider/VoiceChannel/livekit/utils';

type VideoCameraInputProps = {
  deviceId: string;
  variant?: 'small' | 'medium';
  style?: React.CSSProperties;
};

export const VideoCameraInput = ({
  deviceId,
  variant = 'small',
  style,
}: VideoCameraInputProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoLoaded = useRef(false);
  const isMounted = useRef(true);

  const [localTrack, setLocalTrack] = useState<LocalVideoTrack | null>();

  const theme = useTheme();
  const trackLock = useMemo(() => new Mutex(), []);

  const { videoFilter, filterParams } = useLivekitRTCState();

  const hasFilter =
    !!videoFilter &&
    (videoFilter === 'blur' || !!filterParams.virtualBackground);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    trackLock.lock().then(async (unlock) => {
      try {
        const track = await createLocalVideoTrack({
          deviceId: deviceId,
          resolution: VideoPresets.h720.resolution,
          processor: hasFilter
            ? getVideoFilter(videoFilter, filterParams)
            : undefined,
        });

        if (!isMounted.current) {
          track.stop();
          return;
        }

        if (!videoRef.current) {
          track.stop();
          return;
        }

        track.attach(videoRef.current);
        setLocalTrack(track);
      } catch (e: unknown) {
        console.error(e);
      } finally {
        unlock();
      }
    });

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (videoLoaded.current || !localTrack) return;

    localTrack.attach(videoRef.current as HTMLVideoElement);
    videoLoaded.current = true;

    return () => {
      videoLoaded.current = false;
      localTrack.detach(videoRef.current as HTMLVideoElement);
      localTrack.stop();
      setLocalTrack(null);
    };
  }, [localTrack]);

  useEffect(() => {
    if (!localTrack) return;

    localTrack.setDeviceId(deviceId);
  }, [deviceId, localTrack]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    changeProcessor();
  }, [
    videoFilter,
    filterParams.blurRadius,
    filterParams.blurDelegate,
    filterParams.virtualBackground,
  ]);

  const changeProcessor = async () => {
    if (!localTrack) return;
    await localTrack.stopProcessor();
    if (!hasFilter) return;
    await localTrack.setProcessor(getVideoFilter(videoFilter, filterParams));
  };

  const sizes = {
    small: {
      width: 360,
      borderRadius: theme.spacing(1),
    },
    medium: {
      width: 520,
      borderRadius: theme.spacing(2),
    },
  };

  const { width, borderRadius } = sizes[variant];

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        ...style,
      }}
    >
      <video
        ref={videoRef}
        id={deviceId}
        autoPlay
        muted
        playsInline
        style={{
          width: width,
          maxWidth: '100%',
          aspectRatio: '16 / 9',
          borderRadius: borderRadius,
          alignSelf: 'center',
          backgroundColor: 'black',
          transform: 'scaleX(-1)',
        }}
      />
    </div>
  );
};
